import { Controller } from "@hotwired/stimulus"

// The Review Activity panel's own behaviour: search, status filter, collapse, and
// the inline reply composer on an expanded row.
//
// Deep-linking to the commented element is NOT handled here — the row's anchor
// carries the `course-builder#refresh` params, exactly like the before-you-submit
// blockers, so the existing navigation path stays the single implementation.
export default class extends Controller {
  static targets = [
    "row", "list", "search", "searchField", "searchToggle", "collapseToggle",
    "filterOption", "thread", "replyField", "noMatch", "filterEmpty", "count",
    "loading", "error", "errorTemplate", "replyTemplate", "earlierRoundsToggle"
  ]
  // `context` carries course_id / course_version_id / course_snapshot_id: the
  // comments endpoint resolves the snapshot under review from them, and without
  // them the reviewer authorization check rejects the write.
  //
  // `defaultFilter` is the status the SERVER already rendered the list with, so the
  // two agree on the opening state and the panel never flashes the full history.
  static values = { commentsUrl: String, context: Object, defaultFilter: String }

  // The reviewer's last filter choice, remembered per browser. It has to outlive the
  // panel: every resolve/dismiss/reply replaces this element wholesale through the
  // review_activity turbo-stream, which disconnects and re-connects the controller —
  // without this, a reviewer who chose "All" would be snapped back to Pending the
  // moment they resolved something.
  static FILTER_STORAGE_KEY = "lxp:course-review-activity-filter"
  // Whether staff chose to see earlier review rounds — remembered for the same reason
  // as the filter: every change re-renders the panel.
  static EARLIER_ROUNDS_STORAGE_KEY = "lxp:course-review-activity-earlier-rounds"

  connect() {
    this.activeFilter = this.storedFilter() || this.defaultFilterValue || "all"
    this.showEarlierRounds = this.hasEarlierRoundsToggleTarget && this.storedEarlierRounds()
    if (this.hasEarlierRoundsToggleTarget) this.earlierRoundsToggleTarget.checked = this.showEarlierRounds

    // The server rendered the list under the DEFAULT filter with earlier rounds hidden;
    // only a remembered choice that differs from that needs the list re-narrowed here.
    if (this.activeFilter !== this.defaultFilterValue || this.showEarlierRounds) this.applyFilters()
    this.markActiveFilterOption()
  }

  toggleEarlierRounds(event) {
    this.showEarlierRounds = event.currentTarget.checked
    this.rememberSetting(this.constructor.EARLIER_ROUNDS_STORAGE_KEY, this.showEarlierRounds ? "1" : "0")
    this.applyFilters()
  }

  toggleSearch() {
    const expanded = this.searchTarget.hidden
    this.searchTarget.hidden = !expanded
    this.searchToggleTarget.setAttribute("aria-expanded", expanded ? "true" : "false")

    if (expanded) {
      this.searchInput()?.focus()
    } else {
      if (this.searchInput()) this.searchInput().value = ""
      this.applyFilters()
    }
  }

  toggleCollapse() {
    const collapsed = this.element.classList.toggle("is-collapsed")
    this.collapseToggleTarget.setAttribute("aria-expanded", collapsed ? "false" : "true")
  }

  selectFilter(event) {
    this.activeFilter = event.currentTarget.dataset.filterKey || "all"
    this.rememberFilter(this.activeFilter)

    this.markActiveFilterOption()
    this.applyFilters()
  }

  markActiveFilterOption() {
    this.filterOptionTargets.forEach((option) => {
      option.classList.toggle("is-active", option.dataset.filterKey === this.activeFilter)
    })
  }

  filter() {
    this.applyFilters()
  }

  applyFilters() {
    const term = (this.searchInput()?.value || "").trim().toLowerCase()
    let visibleCount = 0

    this.rowTargets.forEach((row) => {
      const matchesTerm = !term || (row.dataset.searchText || "").includes(term)
      const matchesFilter = this.activeFilter === "all" || row.dataset.commentStatus === this.activeFilter
      const matchesRound = this.showEarlierRounds || row.dataset.earlierRound !== "true"
      const visible = matchesTerm && matchesFilter && matchesRound

      row.hidden = !visible
      if (visible) visibleCount += 1
    })

    if (this.hasCountTarget) this.countTarget.textContent = visibleCount

    // Two different "nothing here" messages, and only one may show: a search that
    // matched nothing is the user's own doing, an empty status filter is news about
    // the review ("No pending comments"). The search wins while a term is typed.
    const searchEmpty = Boolean(term) && visibleCount === 0
    if (this.hasNoMatchTarget) this.noMatchTarget.hidden = !searchEmpty

    this.filterEmptyTargets.forEach((message) => {
      message.hidden = searchEmpty || visibleCount > 0 || message.dataset.filterKey !== this.activeFilter
    })
  }

  // The kebab and the row label both open the thread; the label additionally
  // navigates, which is why they are separate actions on the same row.
  toggleThread(event) {
    const thread = event.currentTarget.closest(".course-review-comment")?.querySelector("[data-course-review-activity-target='thread']")
    if (!thread) return

    thread.hidden = !thread.hidden
    if (!thread.hidden) thread.querySelector("input")?.focus()
  }

  openThread(event) {
    const thread = event.currentTarget.closest(".course-review-comment")?.querySelector("[data-course-review-activity-target='thread']")
    if (thread) thread.hidden = false
  }

  async submitReply(event) {
    if (event.key !== "Enter" || event.shiftKey) return

    event.preventDefault()

    const field = event.currentTarget
    const body = field.value.trim()
    if (!body || !this.hasCommentsUrlValue) return

    field.disabled = true
    this.hideError()

    try {
      const response = await fetch(this.commentsUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": this.csrfToken() },
        body: JSON.stringify({
          ...this.contextValue,
          comment: {
            parent_id: field.dataset.parentId,
            lesson_content_id: field.dataset.lessonContentId,
            section: field.dataset.section || null,
            child_key: field.dataset.childKey || null,
            body
          }
        })
      })

      const payload = await response.json()
      if (!response.ok || !payload.success) throw new Error("reply failed")

      this.appendReply(field, payload.comment)
      field.value = ""
      // Programmatic resets fire no input event, so input-state would keep the clear icon.
      field.dispatchEvent(new Event("input", { bubbles: true }))
    } catch (_error) {
      this.showError()
    } finally {
      field.disabled = false
      field.focus()
    }
  }

  // Markup comes from the server-rendered <template>, never from string building.
  appendReply(field, comment) {
    const thread = field.closest("[data-course-review-activity-target='thread']")
    if (!thread || !this.hasReplyTemplateTarget) return

    let replies = thread.querySelector(".course-review-comment__replies")
    if (!replies) {
      replies = document.createElement("ul")
      replies.className = "course-review-comment__replies"
      thread.prepend(replies)
    }

    const reply = this.replyTemplateTarget.content.firstElementChild.cloneNode(true)
    reply.querySelector(".course-review-comment__reply-author").textContent = comment.author_name
    reply.querySelector(".course-review-comment__reply-body").textContent = comment.body
    replies.appendChild(reply)
  }

  // ---- Row actions ----------------------------------------------------------

  resolveComment(event) {
    this.setStatus(event, "resolved")
  }

  dismissComment(event) {
    this.setStatus(event, "dismissed")
  }

  reopenComment(event) {
    this.setStatus(event, "pending")
  }

  setStatus(event, status) {
    event.preventDefault()

    this.changeComment(event.currentTarget.closest(".course-review-comment"), {
      method: "PATCH",
      comment: { status }
    })
  }

  deleteComment(event) {
    event.preventDefault()

    this.changeComment(event.currentTarget.closest(".course-review-comment"), { method: "DELETE" })
  }

  archiveComment(event) {
    event.preventDefault()

    this.changeComment(event.currentTarget.closest(".course-review-comment"), { method: "POST", action: "archive" })
  }

  unarchiveComment(event) {
    event.preventDefault()

    this.changeComment(event.currentTarget.closest(".course-review-comment"), { method: "POST", action: "unarchive" })
  }

  // The row, the modal summary and the element's avatar are all server-rendered, so
  // a successful change asks for a re-render instead of editing any of them here —
  // the same event the composer fires, handled by course-review-sync.
  async changeComment(row, { method, comment = null, action = null }) {
    if (!row || !this.hasCommentsUrlValue) return

    this.hideError()
    const url = [this.commentsUrlValue, row.dataset.commentId, action].filter(Boolean).join("/")

    try {
      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: JSON.stringify({ ...this.contextValue, ...(comment ? { comment } : {}) })
      })

      const payload = await response.json()
      if (!response.ok || !payload.success) {
        throw Object.assign(new Error("comment change failed"), { serverErrors: payload.errors })
      }

      window.dispatchEvent(new CustomEvent("course-review:changed"))
    } catch (error) {
      this.showError(error.serverErrors)
    }
  }

  searchInput() {
    return this.hasSearchFieldTarget ? this.searchFieldTarget : null
  }

  // localStorage throws outright in some privacy modes, so both sides are guarded:
  // a browser that refuses to remember simply opens on the default every time.
  storedFilter() {
    try {
      const stored = window.localStorage.getItem(this.constructor.FILTER_STORAGE_KEY)
      return this.knownFilter(stored) ? stored : null
    } catch (_error) {
      return null
    }
  }

  rememberFilter(filter) {
    this.rememberSetting(this.constructor.FILTER_STORAGE_KEY, filter)
  }

  storedEarlierRounds() {
    try {
      return window.localStorage.getItem(this.constructor.EARLIER_ROUNDS_STORAGE_KEY) === "1"
    } catch (_error) {
      return false
    }
  }

  rememberSetting(key, value) {
    try {
      window.localStorage.setItem(key, value)
    } catch (_error) {
      // Not remembering the choice is not worth failing the click over.
    }
  }

  // A stored value is only honoured while the dropdown still offers it — a filter
  // dropped from the menu must not leave a reviewer stuck on an empty list.
  knownFilter(filter) {
    return Boolean(filter) && this.filterOptionTargets.some((option) => option.dataset.filterKey === filter)
  }

  // The server's own reason (e.g. "You are not reviewing this course.") replaces the
  // generic title when the response carried one, so a refused change says why.
  showError(serverErrors = null) {
    if (!this.hasErrorTarget || !this.hasErrorTemplateTarget) return

    const snackbar = this.errorTemplateTarget.content.firstElementChild.cloneNode(true)
    const message = Array.isArray(serverErrors) ? serverErrors.filter(Boolean).join(" ") : ""
    const title = snackbar.querySelector(".snackbar-title span")
    if (message && title) title.textContent = message

    this.errorTarget.replaceChildren(snackbar)
    this.errorTarget.hidden = false
  }

  hideError() {
    if (!this.hasErrorTarget) return

    this.errorTarget.hidden = true
    this.errorTarget.replaceChildren()
  }

  csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }
}
